import React, { useState, useEffect } from "react";
import { utilityAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { Zap, Droplets, Flame, Fuel, Plus, Trash2, Download, RefreshCw } from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import { useConfirm } from "../hooks/useConfirm";

const UTILITY_TYPES = [
  { value: "electricity", label: "Electricity", icon: Zap, color: "text-yellow-600", bg: "bg-yellow-50", unit: "kWh" },
  { value: "water", label: "Water", icon: Droplets, color: "text-blue-600", bg: "bg-blue-50", unit: "KL" },
  { value: "gas_lpg", label: "Gas / LPG", icon: Flame, color: "text-orange-600", bg: "bg-orange-50", unit: "cylinders" },
  { value: "diesel", label: "Diesel / Fuel", icon: Fuel, color: "text-[#8A7E73]", bg: "bg-[#F4F3F0]", unit: "litres" },
  { value: "other", label: "Other", icon: Plus, color: "text-[#5C5147]", bg: "bg-[#F4F3F0]", unit: "" },
];

export default function UtilityCosts() {
  const { selectedKitchen, kitchens: ctxKitchens } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  const { confirm, ConfirmModal } = useConfirm();

  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [filterType, setFilterType] = useState("");

  const [form, setForm] = useState({
    month: new Date().getMonth() + 1,
    year: currentYear,
    utility_type: "electricity",
    units_consumed: "",
    unit_rate: "",
    total_amount: "",
    meter_reading_start: "",
    meter_reading_end: "",
    bill_number: "",
    notes: "",
  });

  useEffect(() => {
    if (ctxKitchens?.length > 0) setKitchens(ctxKitchens);
    else usersAPI.getKitchens().then(r => setKitchens(r.data || [])).catch(() => {});
  }, [ctxKitchens]);

  useEffect(() => { fetchAll(); }, [year, selectedKitchen, filterType]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const p = { year, kitchen_id: selectedKitchen || null };
      if (filterType) p.utility_type = filterType;
      const [list, summ] = await Promise.all([
        utilityAPI.getCosts(p),
        utilityAPI.getSummary({ year, kitchen_id: selectedKitchen || null })
      ]);
      setCosts(list.data || []);
      setSummary(summ.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!form.total_amount) { setError("Total amount is required"); return; }
    try {
      await utilityAPI.createCost({ ...form, kitchen_id: selectedKitchen || null });
      setMsg("Utility cost saved!");
      setShowForm(false);
      setForm({ month: new Date().getMonth() + 1, year: currentYear, utility_type: "electricity", units_consumed: "", unit_rate: "", total_amount: "", meter_reading_start: "", meter_reading_end: "", bill_number: "", notes: "" });
      fetchAll();
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Delete this utility cost entry?")) return;
    await utilityAPI.deleteCost(id);
    fetchAll();
  };

  const getTypeInfo = (type) => UTILITY_TYPES.find(t => t.value === type) || UTILITY_TYPES[4];
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div className="space-y-5 max-w-5xl" data-testid="utility-costs">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Utility & Overhead Costs</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Electricity, water, gas and other overhead costs per kitchen</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1">
          <Plus size={14} /> Add Entry
        </button>
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{error}</div>}
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-3 text-sm">{msg}</div>}

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
          {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex gap-1">
          {["", ...UTILITY_TYPES.map(t => t.value)].map(t => (
            <button key={t} onClick={() => setFilterType(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${filterType === t ? "bg-[#E8956D] text-[#3A322C] border-[#E8956D]" : "border-[#E5E1DB] text-[#5C5147]"}`}>
              {t ? UTILITY_TYPES.find(u => u.value === t)?.label : "All"}
            </button>
          ))}
        </div>
        {loading && <RefreshCw size={14} className="animate-spin text-[#E8956D]" />}
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 col-span-2">
            <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Annual Total {year}</p>
            <p className="text-3xl font-extrabold text-[#E8956D]">₹{summary.annual_total?.toLocaleString()}</p>
            {summary.cost_per_meal > 0 && (
              <p className="text-xs text-[#8A7E73] mt-1">₹{summary.cost_per_meal} per student-meal-day</p>
            )}
          </div>
          {(summary.by_type || []).map(t => {
            const info = getTypeInfo(t.type);
            return (
              <div key={t.type} className={`${info.bg} border border-[#E5E1DB] rounded-xl p-4`}>
                <p className={`text-xs uppercase font-bold mb-1 ${info.color}`}>{t.type.replace(/_/g," ")}</p>
                <p className={`text-xl font-extrabold ${info.color}`}>₹{t.total?.toLocaleString()}</p>
                <p className="text-[10px] text-[#8A7E73]">{t.months} months recorded</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Entry Form */}
      {showForm && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-4">
          <h3 className="font-bold text-[#3A322C]">New Utility Entry</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Month</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                value={form.month} onChange={e => setForm(f => ({ ...f, month: Number(e.target.value) }))}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Year</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                value={form.year} onChange={e => setForm(f => ({ ...f, year: Number(e.target.value) }))}>
                {[currentYear, currentYear-1, currentYear-2].map(y => <option key={y} value={y}>{y}</option>)}
              </select></div>
            <div className="col-span-2"><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Utility Type</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                value={form.utility_type} onChange={e => setForm(f => ({ ...f, utility_type: e.target.value }))}>
                {UTILITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Units Consumed</label>
              <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                placeholder="e.g. 1200" value={form.units_consumed} onChange={e => setForm(f => ({ ...f, units_consumed: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Rate per Unit (₹)</label>
              <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                placeholder="e.g. 8.50" value={form.unit_rate} onChange={e => setForm(f => ({ ...f, unit_rate: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Total Amount (₹) *</label>
              <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                placeholder="e.g. 12500" value={form.total_amount} onChange={e => setForm(f => ({ ...f, total_amount: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Bill Number</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                value={form.bill_number} onChange={e => setForm(f => ({ ...f, bill_number: e.target.value }))} /></div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
            <button onClick={handleSave} className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2.5 text-sm font-bold">Save</button>
          </div>
        </div>
      )}

      {/* Monthly breakdown */}
      {summary?.monthly?.length > 0 && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
          <h3 className="font-bold text-[#3A322C] mb-3 text-sm">Monthly Trend — {year}</h3>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {summary.monthly.map(m => {
              const maxVal = Math.max(...summary.monthly.map(x => x.total), 1);
              const pct = Math.round(m.total / maxVal * 100);
              return (
                <div key={m.month} className="flex flex-col items-center gap-1 min-w-[50px]">
                  <span className="text-xs font-bold text-[#3A322C]">₹{(m.total / 1000).toFixed(1)}k</span>
                  <div className="w-8 bg-[#F4F3F0] rounded-sm overflow-hidden" style={{ height: 60 }}>
                    <div className="w-full bg-[#E8956D] rounded-sm transition-all" style={{ height: `${pct}%`, marginTop: `${100 - pct}%` }} />
                  </div>
                  <span className="text-[10px] text-[#8A7E73]">{MONTHS[m.month - 1]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
        <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
          <h3 className="font-bold text-[#3A322C] text-sm">All Entries ({costs.length})</h3>
          <button onClick={() => exportCSV(costs.map(c => ({ month: c.month, year: c.year, type: c.utility_type, units: c.units_consumed, rate: c.unit_rate, amount: c.total_amount, bill: c.bill_number })), `utility_${year}`)}
            className="text-xs text-[#E8956D] font-bold flex items-center gap-1"><Download size={11} /> Export</button>
        </div>
        {costs.length === 0 ? (
          <div className="text-center py-10 text-[#8A7E73] text-sm">No entries for {year}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8F8F6]">
              <tr>{["Month", "Type", "Units", "Rate", "Amount", "Bill No.", ""].map(h => (
                <th key={h} className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-[#F4F3F0]">
              {costs.map(c => {
                const info = getTypeInfo(c.utility_type);
                return (
                  <tr key={c.id} className="hover:bg-[#FAFAF8]">
                    <td className="px-4 py-3 font-bold text-[#3A322C]">{MONTHS[c.month - 1]} {c.year}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${info.bg} ${info.color}`}>{c.utility_type?.replace(/_/g," ")}</span></td>
                    <td className="px-4 py-3 text-[#5C5147]">{c.units_consumed ? `${c.units_consumed} ${info.unit}` : "—"}</td>
                    <td className="px-4 py-3 text-[#5C5147]">{c.unit_rate ? `₹${c.unit_rate}` : "—"}</td>
                    <td className="px-4 py-3 font-bold text-[#E8956D]">₹{Number(c.total_amount).toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-[#8A7E73] font-mono">{c.bill_number || "—"}</td>
                    <td className="px-4 py-3"><button onClick={() => handleDelete(c.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={13} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {ConfirmModal}
    </div>
  );
}
